import {
  getSupabaseAnonKey,
  getSupabaseUrl,
  supabase,
} from '@/lib/supabase';
import { withColdStartRetry } from '@/lib/coldStartRetry';

export type OpenRouterOperationalLevel = 'healthy' | 'degraded' | 'stale' | 'unknown';

export interface OpenRouterOperationalStatus {
  generatedAt: string;
  status: OpenRouterOperationalLevel;
  label: string;
  account: {
    status: OpenRouterOperationalLevel;
    reason: string;
    observedAt: string | null;
    snapshotId: string | null;
    persistence: 'persisted' | 'unverified';
    ageSeconds: number | null;
    monitoringConfigured: boolean;
    lastErrorCode: string | null;
    credit?: {
      totalCredits: number | null;
      totalUsage: number | null;
      remaining: number | null;
    };
    keyInventory?: {
      scope: 'all_workspaces';
      workspaceCount: number;
      scannedWorkspaceCount: number;
      complete: boolean;
    };
    keys?: {
      total: number;
      active: number;
      disabled: number;
      expired: number;
      expiringWithinSevenDays: number;
      exhausted: number;
      lowRemaining: number;
      usageDaily: number;
      usageMonthly: number;
    };
    activity30d?: {
      requests: number;
      costUsd: number;
      promptTokens: number;
      completionTokens: number;
      reasoningTokens: number;
      topModels: Array<{
        model: string;
        provider: string | null;
        requests: number;
        costUsd: number;
        promptTokens: number;
        completionTokens: number;
        reasoningTokens: number;
      }>;
    };
  };
  broadcast: {
    status: OpenRouterOperationalLevel;
    expected: boolean;
    lastTraceAt: string | null;
    ageSeconds: number | null;
    traces24h: number;
    readPath: 'typed_columns_only';
    upstreamPrivacyMode: 'dashboard_not_api_verifiable';
  };
  viewer: {
    role: string;
    detail: 'admin' | 'staff_summary';
  };
}

function isOperationalLevel(value: unknown): value is OpenRouterOperationalLevel {
  return ['healthy', 'degraded', 'stale', 'unknown'].includes(String(value));
}

export const openRouterAccountObservabilityService = {
  async getStatus(signal?: AbortSignal): Promise<OpenRouterOperationalStatus> {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) throw sessionError;
    if (!session?.access_token) throw new Error('OpenRouter account status requires authentication.');

    // The monitor is low-traffic and goes cold; the first dashboard fetch after
    // a cold period can fail its CORS preflight during boot, which would render
    // the pill "degraded" even when the snapshot is healthy. Retry the fetch (an
    // aborted request or a real HTTP error response is not retried).
    const response = await withColdStartRetry(() => fetch(
      `${getSupabaseUrl()}/functions/v1/openrouter-account-observability`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          apikey: getSupabaseAnonKey(),
          Accept: 'application/json',
        },
        signal,
      },
    ));
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(
        typeof payload?.error === 'string'
          ? payload.error.slice(0, 240)
          : `OpenRouter account status failed: HTTP ${response.status}`,
      );
    }
    if (!payload || typeof payload !== 'object' || !isOperationalLevel(payload.status)) {
      throw new Error('OpenRouter account status returned an invalid response.');
    }
    return payload as OpenRouterOperationalStatus;
  },
};
